<template>
    <edit-resource
        :form="form"
        :title="title"
        class="w-480"
        customClass="max-w-640"
        @close="$emit('close')"
        @submit="addField"
    >
        <select-dropdown
            v-model="form.question_type"
            :options="fieldTypes"
            class="mb-16"
        >
            {{ $t('question.select_type') }}
        </select-dropdown>

        <div class="mb-32">
            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs">
                <div v-for="lang in languages" :key="lang">
                    <simple-input
                        v-show="lang === selectedLanguage"
                        v-model="form.question[lang]"
                        autofocus
                        class="mb-16"
                    >
                        {{ $t('question.question') }}
                    </simple-input>
                    <div v-if="lang === selectedLanguage && form.question_type === 'multiple_choice'">
                        <div class="first-letter:uppercase mb-8">{{ $t('question.options') }}</div>
                        <draggable
                            v-model="form.options[lang]"
                            handle=".draggable"
                            :item-key="(item, index) => `options-${lang}-${index}`"
                        >
                            <template #item="{ element, index }">
                                <div class="flex items-center mb-8">
                                    <simple-input
                                        v-model="form.options[lang][index]"
                                        class="flex-grow mr-8"
                                    ></simple-input>
                                    <button
                                        class="flex-none h-40 w-40 flex items-center justify-center text-primary-80 hover:text-semantic-error trans-ease"
                                        @click="removeOption(index)"
                                    >
                                        <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                                    </button>
                                    <div class="h-full pt-12 pl-16 flex flex-col">
                                        <div class="draggable rounded text-primary-80 bg-primary-5 w-40 h-full flex items-center justify-center cursor-move mb-12 mr-12">
                                            <svg-icon type="drag-handle" class="max-h-32 max-w-16"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                        <div class="flex">
                            <simple-button
                                class="flex-grow mr-48"
                                icon="add-circle"
                                @click="addOption"
                            >
                                {{ $t('question.add_option') }}
                            </simple-button>
                        </div>
                    </div>
                </div>
            </translatable>
        </div>

        <toggle
            v-if="['free_text', 'multiple_choice'].includes(form.question_type)"
            v-model="form.multiple"
            class="mb-16"
        >
            {{ form.question_type === 'free_text' ? $t('question.multiple_lines') : $t('question.multiple_choice_multiple') }}
        </toggle>

        <toggle
            v-if="['yes_no'].includes(form.question_type)"
            v-model="form.disqualify_answer_no"
            class="mb-16"
        >
            {{ $t('question.disqualify_answer_no') }}
        </toggle>

        <toggle v-model="form.required"
                @change="checkToggle($event)">
            {{ $t('question.required') }}
        </toggle>

    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Translatable from '../forms/Translatable.vue';
import Toggle from '../forms/Toggle.vue';
import Form from '../../etc/forms/Form';
import SelectDropdown from '../forms/SelectDropdown.vue';
import SimpleButton from './SimpleButton.vue';
import SvgIcon from './SvgIcon.vue';
import {mapState, mapActions} from 'pinia';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import draggable from 'vuedraggable';
// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

function getFields() {
    return [
        {name: 'disqualify_answer_no', value: false},
        {name: 'required_answer_yes', value: false},
        {name: 'multiple', value: false},
        {name: 'required', value: true},
        {name: 'question_type', value: 'free_text'},
        {name: 'question', value: {}, translatable: true},
        {name: 'options', value: ['', '', ''], translatable: true},
        {name: 'key', value: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)},
    ];
}

export default {
    components: {
        'edit-resource': EditResource,
        'simple-input': Input,
        'translatable': Translatable,
        'toggle': Toggle,
        'select-dropdown': SelectDropdown,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'draggable': draggable,
    },
    props: {
        resource: Object,
        exclude_types: Array,
    },
    data: function () {
        return {
            form: this.generateForm(),
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        title() {
            return this.$t(this.field ? 'general.edit_subject' : 'general.add_subject', {subject: this.$t('jobs.form_field')});
        },
        fieldTypes() {
            let types = [
                {value: 'free_text', label: 'question.free_text'},
                {value: 'multiple_choice', label: 'question.multiple_choice'},
                {value: 'yes_no', label: 'question.yes_no'},
                {value: 'file', label: 'question.file'},
            ];

            if (this.exclude_types) {
                //Remove excluded types
                let removeObj = {};

                this.exclude_types.forEach(e => removeObj[e] = true);

                types = types.filter(function (val) {
                    return !removeObj[val.value];
                });
            }

            return types;
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.question[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        generateForm() {
            let fields = getFields();

            if (this.resource) {
                fields.forEach((field, index) => {
                    if (this.resource.hasOwnProperty(field.name)) {
                        fields[index].value = JSON.parse(JSON.stringify(this.resource[field.name]));
                    }
                });
            }
            return new Form(fields);
        },
        addField() {
            this.form.submit(data => {
                return new Promise((resolve, reject) => {
                    this.$emit('input', {
                        data,
                        success: () => {
                            resolve();
                        },
                        error: (msg) => {
                            reject(new Error(msg));
                        },
                    });
                });
            }).then(() => {
                this.$emit('close');
            }).catch((error) => {
                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: error.message,
                });
            });
        },
        addOption() {
            Object.keys(this.form.options).forEach(lang => {
                this.form.options[lang] = [...this.form.options[lang], ''];
            });
        },
        removeOption(index) {
            Object.keys(this.form.options).forEach(lang => {
                let currentOptions = this.form.options[lang];
                currentOptions.splice(index, 1);
                this.form.options[lang] = currentOptions;
            });
        },
        setRequired(state) {
            if (state) {
                this.form.required = true;
            }
        },
        checkToggle(state) {
            if (!state && this.form.required_answer_yes) {
                this.form.required_answer_yes = false;
            }
        },
    }
};
</script>
